import React, { useMemo, useState } from 'react';
import { retireRep, deleteRep } from '../services/adminService';
import { monthBounds, formatDate } from '../utils/period';
import { apiErrorMessage } from '../utils/apiError';

// Temsilci silme akışı. Temsilcinin açık brick atamaları seçilen ayın sonunda kapatılır,
// ertesi ayın 1'i itibarıyla aynı brick ve oranlarla yeni atamalar açılır; ardından
// temsilci silinir. Tümü backend'de tek transaction içinde yapılır.
const RepRetireModal = ({ rep, repBricks, reps, regions, tid, onClose, onDone }) => {
  const openBricks = useMemo(
    () => repBricks.filter(rb => rb.repId === rep.id && !rb.endDate),
    [repBricks, rep.id]
  );

  const [month, setMonth] = useState('');
  const [rows, setRows] = useState(() =>
    openBricks.map(rb => ({ repBrickId: rb.id, repId: '', whiteFlag: false, regionId: '' }))
  );
  const [bulkRepId, setBulkRepId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { endDate, nextStart } = monthBounds(month);

  // Devir hedefleri: aynı ekipteki diğer temsilciler. Ekipte başka temsilci yoksa tüm liste.
  const sameTeamReps = reps.filter(r => r.id !== rep.id && r.teamName === rep.teamName);
  const candidateReps = sameTeamReps.length > 0 ? sameTeamReps : reps.filter(r => r.id !== rep.id);
  const candidateRegions = regions.filter(r => r.teamName === rep.teamName);

  // Kapanış, en geç başlayan atamadan sonra olmalı — o ayın öncesi seçilemez.
  const minMonth = openBricks
    .map(rb => (rb.startDate || '').substring(0, 7))
    .filter(Boolean)
    .sort()
    .pop();
  const maxMonth = `${new Date().getFullYear() + 1}-12`;

  const brickById = (id) => openBricks.find(rb => rb.id === id);
  const updateRow = (id, patch) =>
    setRows(prev => prev.map(r => (r.repBrickId === id ? { ...r, ...patch } : r)));

  const applyBulk = () => {
    if (!bulkRepId) return;
    setRows(prev => prev.map(r => ({ ...r, repId: bulkRepId, whiteFlag: false, regionId: '' })));
  };

  const unresolved = rows.filter(r => (r.whiteFlag ? !r.regionId : !r.repId)).length;
  const canSave = openBricks.length === 0
    ? true
    : Boolean(endDate) && unresolved === 0;

  const handleSave = async () => {
    setError('');
    if (openBricks.length > 0) {
      if (!endDate) { setError('Kapanış ayını seçin.'); return; }
      if (unresolved > 0) { setError(`${unresolved} brick için yeni temsilci veya bölge seçilmedi.`); return; }
    }
    setSaving(true);
    try {
      if (openBricks.length === 0) {
        await deleteRep(rep.id, tid);
      } else {
        await retireRep(
          rep.id,
          endDate,
          rows.map(r => ({
            repBrickId: r.repBrickId,
            repId: r.whiteFlag ? null : parseInt(r.repId, 10),
            whiteFlag: r.whiteFlag,
            regionId: r.whiteFlag ? parseInt(r.regionId, 10) : null,
          })),
          tid
        );
      }
      onDone(openBricks.length === 0
        ? 'Temsilci silindi.'
        : `Temsilci silindi; ${openBricks.length} brick ataması devredildi.`);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={saving ? undefined : onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <h3 style={styles.title}>Temsilci Silme — {rep.repName}</h3>

        {openBricks.length === 0 ? (
          <p style={styles.desc}>
            Bu temsilcinin açık brick ataması yok. Temsilci doğrudan silinecek.
          </p>
        ) : (
          <>
            <p style={styles.desc}>
              Temsilcinin <strong>{openBricks.length}</strong> açık brick ataması seçilen ayın
              sonunda kapatılacak; aynı brick'ler için ertesi ayın 1'i itibarıyla yeni atamalar
              açılacak. Oranlar mevcut atamalardan aynen taşınır.
            </p>

            <div style={styles.periodRow}>
              <div style={styles.field}>
                <label style={styles.label}>Kapanış Ayı *</label>
                <input
                  type="month" value={month} min={minMonth} max={maxMonth}
                  onChange={e => setMonth(e.target.value)}
                  style={{ ...styles.input, borderColor: '#dc2626' }}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Dönem</label>
                <div style={styles.periodInfo}>
                  {endDate
                    ? `Kapanış: ${formatDate(endDate)} · Yeni başlangıç: ${formatDate(nextStart)}`
                    : '— ay seçilince hesaplanır —'}
                </div>
              </div>
            </div>

            <div style={styles.bulkRow}>
              <label style={styles.label}>Tüm brick'leri şu temsilciye ata:</label>
              <select
                style={{ ...styles.input, minWidth: '240px' }}
                value={bulkRepId}
                onChange={e => setBulkRepId(e.target.value)}
              >
                <option value="">Temsilci seçin...</option>
                {candidateReps.map(r => (
                  <option key={r.id} value={r.id}>{r.regionName} → {r.repName}</option>
                ))}
              </select>
              <button
                style={{ ...styles.secondaryButton, opacity: bulkRepId ? 1 : 0.5 }}
                disabled={!bulkRepId}
                onClick={applyBulk}
              >
                Tümüne Uygula
              </button>
              <span style={{ fontSize: '12px', color: unresolved ? '#dc2626' : '#166534' }}>
                {unresolved ? `${unresolved} satır eksik` : 'Tüm satırlar hazır'}
              </span>
            </div>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Brick</th>
                    <th style={styles.th}>Oran %</th>
                    <th style={styles.th}>Mevcut Başlangıç</th>
                    <th style={styles.th}>Yeni Başlangıç</th>
                    <th style={styles.th}>Boşta</th>
                    <th style={styles.th}>Yeni Temsilci / Bölge</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const rb = brickById(row.repBrickId);
                    return (
                      <tr key={row.repBrickId}>
                        <td style={styles.td}><strong>{rb?.brickName}</strong></td>
                        <td style={styles.td}>{rb?.splitRatio}%</td>
                        <td style={styles.td}>{rb?.startDate}</td>
                        <td style={{ ...styles.td, color: '#166534', fontWeight: 600 }}>
                          {nextStart || <span style={{ color: '#94a3b8', fontWeight: 400 }}>ay seçin</span>}
                        </td>
                        <td style={styles.td}>
                          <input
                            type="checkbox" checked={row.whiteFlag}
                            onChange={e => updateRow(row.repBrickId, {
                              whiteFlag: e.target.checked, repId: '', regionId: '',
                            })}
                          />
                        </td>
                        <td style={styles.td}>
                          {row.whiteFlag ? (
                            <select
                              style={styles.input}
                              value={row.regionId}
                              onChange={e => updateRow(row.repBrickId, { regionId: e.target.value })}
                            >
                              <option value="">Bölge seçin...</option>
                              {candidateRegions.map(r => (
                                <option key={r.id} value={r.id}>{r.regionName}</option>
                              ))}
                            </select>
                          ) : (
                            <select
                              style={styles.input}
                              value={row.repId}
                              onChange={e => updateRow(row.repBrickId, { repId: e.target.value })}
                            >
                              <option value="">Temsilci seçin...</option>
                              {candidateReps.map(r => (
                                <option key={r.id} value={r.id}>{r.regionName} → {r.repName}</option>
                              ))}
                            </select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.actions}>
          <button
            style={{ ...styles.dangerButton, opacity: (saving || !canSave) ? 0.6 : 1 }}
            disabled={saving || !canSave}
            onClick={handleSave}
          >
            {saving ? 'Kaydediliyor...' : (openBricks.length === 0 ? 'Temsilciyi Sil' : 'Devret ve Sil')}
          </button>
          <button style={styles.cancelButton} onClick={onClose} disabled={saving}>İptal</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
    justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '16px',
  },
  modal: {
    backgroundColor: 'white', padding: '24px', borderRadius: '8px',
    width: '900px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  },
  title: { margin: '0 0 12px', color: '#1a1a2e', fontSize: '18px' },
  desc: { fontSize: '13px', color: '#64748b', margin: '0 0 16px', lineHeight: 1.5 },
  periodRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', color: '#64748b', fontWeight: 600 },
  input: {
    padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '6px',
    fontSize: '13px', minWidth: '180px',
  },
  periodInfo: {
    padding: '8px 10px', border: '1px solid #166534', borderRadius: '6px',
    backgroundColor: '#e0f2fe', color: '#166534', fontSize: '13px', fontWeight: 600,
    minWidth: '340px',
  },
  bulkRow: {
    display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap',
    padding: '12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: '6px', marginBottom: '12px',
  },
  tableWrap: { overflowX: 'auto', maxHeight: '340px', overflowY: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px', borderBottom: '2px solid #e2e8f0',
    fontSize: '12px', color: '#64748b', position: 'sticky', top: 0, backgroundColor: 'white',
  },
  td: { padding: '8px', borderBottom: '1px solid #f1f5f9' },
  error: {
    margin: '12px 0 0', padding: '10px', borderRadius: '6px',
    backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '13px',
  },
  actions: { display: 'flex', gap: '8px', marginTop: '20px' },
  dangerButton: {
    padding: '8px 16px', backgroundColor: '#dc2626', color: 'white',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
  },
  secondaryButton: {
    padding: '8px 14px', backgroundColor: '#1a1a2e', color: 'white',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
  },
  cancelButton: {
    padding: '8px 16px', backgroundColor: 'white', color: '#64748b',
    border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
  },
};

export default RepRetireModal;
